import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators
} from '@angular/forms';

import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { first } from 'rxjs';

import { AuthService } from 'app/auth/auth.service';
import { ToastService } from '../../services/toast.service';

import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { AuthFormComponent } from '../auth-form/auth-form.component';
import { InputComponent } from '../input/input.component';
import { ButtonComponent } from '../button/button.component';
import { ResendCodeComponent } from '../resend-code/resend-code.component';

import { ISvgConfig } from '../../interfaces/svg.interfaces';
import { IInputCustomConfig } from 'app/shared/interfaces/input.interfaces';
import { IButtonConfig } from 'app/shared/interfaces/button.interfaces';

import { ESvgTypes } from '../../enums/svg.enums';
import { EButtonTypes } from 'app/shared/enums/button.enums';
import { EInputCustomType, EInputSupportMessage, EInputUsage } from 'app/shared/enums/input.enums';
import { EToastType } from 'app/shared/enums/toast.enums';
import { EResendCodeUsage } from 'app/shared/enums/shared.enums';

import { PASSWORD_VALIDATOR_PATTERN } from 'app/shared/const/app.constants';

@Component({
  selector: 'stxt-password-reset',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    SvgIconComponent,
    InputComponent,
    ButtonComponent,
    ResendCodeComponent
  ],
  templateUrl: './password-reset.component.html',
  styleUrl: './password-reset.component.scss'
})
export class PasswordResetComponent implements OnInit {
  @ViewChild('resendCode') resendCode!: ResendCodeComponent;
  public closeSvg: ISvgConfig = { fill: ESvgTypes.OutlinedDark, name: 'close' };
  public passwordResetForm: FormGroup;
  public emailInput: IInputCustomConfig = {
    placeholder: 'Email',
    supportingText: {
      text: EInputSupportMessage.Email
    },
    type: EInputCustomType.Email,
    usageCase: EInputUsage.Page
  };
  public codeInput: IInputCustomConfig = {
    placeholder: 'Password reset code',
    supportingText: {
      text: EInputSupportMessage.VerifyCodeError
    },
    type: EInputCustomType.Text,
    usageCase: EInputUsage.Page
  };

  public newPasswordInput: IInputCustomConfig = {
    placeholder: 'New password',
    supportingText: {
      text: EInputSupportMessage.Password
    },
    type: EInputCustomType.Password,
    usageCase: EInputUsage.Page
  };

  public userNotFoundError: boolean = false;
  public getResendCodeButton: IButtonConfig = {
    fill: EButtonTypes.SecondaryFilled,
    text: 'Get reset code'
  };
  public submitCodeButton: IButtonConfig = {
    fill: EButtonTypes.SecondaryFilled,
    text: 'Submit'
  };
  protected readonly EResendCodeUsage = EResendCodeUsage;
  public resetCodeSent: boolean = false;

  constructor(
    public readonly activeModal: NgbActiveModal,
    private readonly modal: NgbModal,
    private readonly formBuilder: FormBuilder,
    private readonly authService: AuthService,
    private readonly toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.initPasswordResetForm();
  }

  initPasswordResetForm(): void {
    this.passwordResetForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email, this.userNotFoundValidator]],
      password: ['', [Validators.required, Validators.pattern(PASSWORD_VALIDATOR_PATTERN)]],
      code: ['', [Validators.required]]
    });
  }

  closeModal(): void {
    this.activeModal.close();
  }

  userNotFoundValidator: ValidatorFn = () => {
    return this.userNotFoundError ? { userNotFound: true } : null;
  };

  onEnterKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onSubmitCode();
    }
  }

  onResendCode(): void {
    this.showToast(
      'Verification code was resent',
      'Please check your email for another code.',
      EToastType.SUCCESS
    );
  }

  private showToast(heading: string, subheading: string, toastType: EToastType): void {
    this.toastService.showToast({
      toastHeading: heading,
      toastSubheading: subheading,
      toastType
    });
  }

  onSendCode(): void {
    const email = this.passwordResetForm.get('email')?.value;

    this.authService
      .forgotPassword(email)
      .pipe(first())
      .subscribe({
        next: () => {
          this.showToast(
            'Reset code sent',
            'Check your email for the reset code.',
            EToastType.SUCCESS
          );
          this.resetCodeSent = true;
        },
        error: () => {
          this.showToast(
            'Error sending reset code',
            'Could not send reset code. Please try again.',
            EToastType.FAIL
          );
        }
      });
  }

  onSubmitCode(): void {
    this.authService
      .resetPassword(this.passwordResetForm.value)
      .pipe(first())
      .subscribe({
        next: () => {
          this.showToast(
            'Password reset successful',
            'Your password has been reset. Please login with your new password.',
            EToastType.SUCCESS
          );
          this.returnToLogin();
        },
        error: () => {
          this.showToast(
            'Error resetting password',
            'Could not reset password. Please try again.',
            EToastType.FAIL
          );
        }
      });
  }

  returnToLogin(): void {
    this.activeModal.close();
    const modalRef =
      window.innerWidth < 640
        ? this.modal.open(AuthFormComponent, {
            size: 'sm',
            windowClass: 'modal-fullscreen'
          })
        : this.modal.open(AuthFormComponent);
    modalRef.componentInstance.isSignUp = false;
  }
}
